import type { LanguageCode, User } from '@repo/domain';

import { LANGUAGE_OPTIONS } from '@shared/constants/languageOptions';

const getDefaultLanguage = (): LanguageCode =>
  (LANGUAGE_OPTIONS.find((option) => option.isDefault) ?? LANGUAGE_OPTIONS[0]).value;

const asSupportedLanguage = (language: LanguageCode | undefined): LanguageCode | undefined =>
  LANGUAGE_OPTIONS.some((option) => option.value === language) ? language : undefined;

const matchBrowserLanguage = (): LanguageCode | undefined => {
  const browserLanguage = window.navigator.language.toLowerCase();
  const match = LANGUAGE_OPTIONS.find((option) =>
    option.localePrefixes.some((prefix) => browserLanguage.startsWith(prefix))
  );
  return match?.value;
};

/**
 * Single source of truth for resolving the active language. Priority:
 * 1. the logged-in user's backend language,
 * 2. the persisted browser choice,
 * 3. the browser/OS locale matched against {@link LANGUAGE_OPTIONS},
 * 4. and only then the default language.
 *
 * All language knowledge (which languages exist, their locale prefixes, the
 * default) lives in {@link LANGUAGE_OPTIONS} — this util derives everything from it.
 */
export const resolveLanguage = (user: User | undefined, storedLanguage: LanguageCode | undefined): LanguageCode => {
  return (
    asSupportedLanguage(user?.language) ??
    asSupportedLanguage(storedLanguage) ??
    matchBrowserLanguage() ??
    getDefaultLanguage()
  );
};
